import iso31662 from "iso-3166-2";

export interface StateOption {
  /** Bare subdivision code (for example `LIM` from `PE-LIM`). */
  code: string;
  /** Full ISO 3166-2 code (for example `PE-LIM`). */
  fullCode: string;
  name: string;
}

const cache = new Map<string, StateOption[]>();

export const statesForCountry = (
  countryAlpha2: string | undefined | null
): StateOption[] => {
  if (!countryAlpha2) return [];
  const upper = countryAlpha2.toUpperCase();
  const cached = cache.get(upper);
  if (cached) return cached;

  const country = (
    iso31662 as unknown as {
      country: (
        code: string
      ) => { sub?: Record<string, { name: string; type: string }> } | null;
    }
  ).country(upper);

  if (!country?.sub) {
    cache.set(upper, []);
    return [];
  }

  const items = Object.entries(country.sub).map(([fullCode, subdivision]) => ({
    code: fullCode.split("-")[1] ?? fullCode,
    fullCode,
    name: subdivision.name,
  }));
  items.sort((a, b) => a.name.localeCompare(b.name));
  cache.set(upper, items);
  return items;
};

/** Resolve a human-readable subdivision name from ISO 3166-2 (fallback: code). */
export const stateName = (
  countryAlpha2: string | undefined | null,
  stateCode: string | undefined | null
): string => {
  if (!stateCode?.trim()) return "";
  const trimmed = stateCode.trim().toUpperCase();
  const bare =
    trimmed.includes("-") && trimmed.indexOf("-") <= 3
      ? trimmed.slice(trimmed.indexOf("-") + 1)
      : trimmed;
  const list = statesForCountry(countryAlpha2);
  const match = list.find(
    (s) =>
      s.code.toUpperCase() === bare ||
      s.fullCode.toUpperCase() === trimmed
  );
  return match?.name ?? bare;
};

export const resolveStateOptions = (
  country: string | undefined | null,
  catalogStates:
    | Array<{ stateCode: string; stateName: string }>
    | null
    | undefined,
  isAnyStateMode: boolean
): Array<{ code: string; name: string }> => {
  if (isAnyStateMode) {
    return statesForCountry(country).map((s) => ({
      code: s.code,
      name: s.name,
    }));
  }
  const isoByCode = new Map(
    statesForCountry(country).map((s) => [s.code.toUpperCase(), s.name])
  );
  return (catalogStates ?? []).map((s) => ({
    code: s.stateCode,
    name: isoByCode.get(s.stateCode?.toUpperCase()) ?? s.stateName,
  }));
};
